import { KEYCLOAK_CLIENT, KEYCLOAK_REALM, KEYCLOAK_URL } from '@m/config/env';
import { User } from '@m/types/api';
import { heapTrackProperties } from '@m/utils/heap';
import Keycloak, { KeycloakOnLoad } from 'keycloak-js';

/**
 * Refresh once the access token has fewer than this many seconds of validity
 * left. This must comfortably exceed the slowest expected request round trip so
 * a token cannot expire while a request is still in flight.
 */
const MIN_TOKEN_VALIDITY_SECONDS = 70;

/**
 * Backoff between refresh attempts that failed for a recoverable reason. Mobile
 * connections drop constantly; a dropped request must never end the session.
 */
const REFRESH_RETRY_DELAYS_MS = [500, 1_500, 4_000];

/** keycloak-js treats a `minValidity` of -1 as "refresh regardless of expiry". */
const FORCE_REFRESH = -1;

/** Upper bound on how often returning to the tab triggers a forced refresh. */
const RESUME_REFRESH_THROTTLE_MS = 60_000;

/** Thrown only when Keycloak actively rejected the refresh token. */
class SessionExpiredError extends Error {
  constructor() {
    super('Keycloak rejected the refresh token');
    this.name = 'SessionExpiredError';
  }
}

let keycloakClient: Keycloak | null = null;
let initPromise: Promise<void> | null = null;
let inFlightRefresh: Promise<boolean> | null = null;
let isRedirectingToLogin = false;
let isKeepAliveRegistered = false;
let lastResumeRefreshAt = 0;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * keycloak-js discards the refresh token only when the server actively rejects
 * it (HTTP 400 / invalid_grant), so a missing token is our signal that the SSO
 * session is genuinely over rather than merely unreachable.
 */
const isSessionExpired = () => !keycloakClient?.refreshToken;

/**
 * Single choke point for auth diagnostics. An unexpected sign-out is otherwise
 * invisible, because the redirect has already navigated the page away by the
 * time anyone could look.
 */
function logSessionEvent(event: string, detail?: unknown) {
  // eslint-disable-next-line no-console
  console.warn(`[keycloak] ${event}`, detail ?? '');
}

function redirectToLogin(reason: string) {
  // keycloak-js already calls login() itself when it clears the token for an
  // app initialized with 'login-required', so guard against a double redirect.
  if (isRedirectingToLogin) {
    return;
  }
  isRedirectingToLogin = true;
  logSessionEvent('redirecting to login', reason);
  void keycloakClient?.login();
}

/**
 * Refreshes the access token, retrying failures that a retry could plausibly
 * fix. Rejects with {@link SessionExpiredError} when the session is over, and
 * with the underlying error when we simply could not reach Keycloak.
 */
async function attemptRefresh(minValidity: number, attempt = 0): Promise<boolean> {
  const client = keycloakClient;
  if (!client) {
    throw new Error('Keycloak is not initialized');
  }

  try {
    return await client.updateToken(minValidity);
  } catch (error) {
    if (isSessionExpired()) {
      throw new SessionExpiredError();
    }
    // Retrying while the device is offline just burns the budget we would
    // rather spend once connectivity is back.
    if (attempt >= REFRESH_RETRY_DELAYS_MS.length || navigator.onLine === false) {
      throw error;
    }
    await sleep(REFRESH_RETRY_DELAYS_MS[attempt]);
    return attemptRefresh(minValidity, attempt + 1);
  }
}

/**
 * Ensures the access token is valid for at least `minValidity` seconds.
 *
 * Concurrent callers share a single attempt; the first caller's `minValidity`
 * wins. Resolves `true` when the token was actually refreshed.
 */
function refreshSession(minValidity = MIN_TOKEN_VALIDITY_SECONDS): Promise<boolean> {
  if (!keycloakClient?.refreshToken) {
    return Promise.reject(new SessionExpiredError());
  }
  if (!inFlightRefresh) {
    inFlightRefresh = attemptRefresh(minValidity).finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

function handleBackgroundRefreshFailure(error: unknown) {
  if (error instanceof SessionExpiredError) {
    redirectToLogin('refresh token rejected during background refresh');
    return;
  }
  // Unreachable Keycloak. Keep the current token; the next request or resume
  // will try again.
  logSessionEvent('background refresh failed, keeping current token', error);
}

/**
 * Refreshing on resume is what keeps mobile sessions alive: background tabs get
 * their timers throttled or frozen, so the proactive refresh never fires while
 * the user is away. Refreshing the moment they come back both restores a usable
 * token and resets the server-side SSO idle timer.
 */
function registerSessionKeepAlive() {
  if (isKeepAliveRegistered || typeof window === 'undefined') {
    return;
  }
  isKeepAliveRegistered = true;

  const refreshOnResume = () => {
    if (document.visibilityState !== 'visible' || !keycloakClient?.refreshToken) {
      return;
    }
    // Resume fires on every tab switch, so throttle rather than refreshing per event.
    const now = Date.now();
    if (now - lastResumeRefreshAt < RESUME_REFRESH_THROTTLE_MS) {
      return;
    }
    lastResumeRefreshAt = now;
    // Forced, because only an actual refresh-token grant resets the server-side
    // SSO idle timer; a token that still passes its validity check would
    // otherwise short-circuit without reaching Keycloak at all.
    void refreshSession(FORCE_REFRESH).catch(handleBackgroundRefreshFailure);
  };

  document.addEventListener('visibilitychange', refreshOnResume);
  window.addEventListener('online', refreshOnResume);
  // iOS restores backgrounded tabs from the back/forward cache without firing
  // visibilitychange, so cover that path too.
  window.addEventListener('pageshow', refreshOnResume);
}

async function startInit(onLoad: KeycloakOnLoad) {
  // Keycloak references window.document so wait until init is called to initialize.
  const client = new Keycloak({
    url: KEYCLOAK_URL,
    realm: KEYCLOAK_REALM,
    clientId: KEYCLOAK_CLIENT,
  });
  keycloakClient = client;

  // These must be assigned before init(): keycloak-js only schedules the
  // token-expiry timer if onTokenExpired is already set when it stores a token.
  client.onTokenExpired = () => {
    void refreshSession().catch(handleBackgroundRefreshFailure);
  };
  // Fires when keycloak-js drops the token on its own, which for a
  // 'login-required' app immediately self-redirects to the login page.
  client.onAuthLogout = () => {
    logSessionEvent('session cleared by keycloak-js');
  };

  // microsoft login hangs and then fails if we initialize keycloak. we don't need to initialize if we're in the login-redirect page
  if (window.location.pathname === '/login-redirect') {
    return;
  }

  await client.init({
    onLoad,
    // The session-status iframe polls Keycloak's check-session endpoint to
    // notice sign-out in another tab. On mobile it costs far more than it
    // provides: updateToken() awaits an un-timed postMessage round trip to an
    // iframe that iOS may have discarded while backgrounded (wedging every
    // request), and any 'error'/'changed' reply clears the token and forces a
    // login. A revoked session is still caught on the next refresh.
    checkLoginIframe: false,
  });

  registerSessionKeepAlive();
}

export const KeycloakService = {
  /**
   * Initializes the Keycloak instance. Safe to call more than once: concurrent
   * and repeat callers share one client, because a second client would race the
   * first for the single-use authorization code in the URL and whichever lost
   * would leave the app unauthenticated.
   *
   * @param onLoad
   * @param onAuthenticatedCallback
   * @param onInitComplete Called after init settles (success, error, or skipped on `/login-redirect`).
   */
  initKeycloak: async (
    onLoad: KeycloakOnLoad,
    onAuthenticatedCallback?: () => void,
    onInitComplete?: () => void,
  ) => {
    // Assigned synchronously so concurrent callers await the same attempt.
    if (!initPromise) {
      initPromise = startInit(onLoad);
    }

    try {
      await initPromise;
      onAuthenticatedCallback?.();
    } catch (error) {
      // Clear the cached attempt so a later mount can retry what is usually a
      // transient connectivity failure, rather than caching it for the life of
      // the page.
      initPromise = null;
      logSessionEvent('init failed', error);
    } finally {
      onInitComplete?.();
    }
  },

  doLogin: () => {
    heapTrackProperties('Login', {
      login_method: 'keycloak',
    });
    return keycloakClient?.login();
  },

  isInit: () => !!keycloakClient,

  isTokenExpired: (minValidity = 60) => keycloakClient?.isTokenExpired(minValidity),

  doLogout: () => keycloakClient?.logout(),

  getToken: () => keycloakClient?.token,

  getTokenParsed: () => keycloakClient?.tokenParsed,

  isLoggedIn: () => !!keycloakClient?.token,

  /**
   * Returns a token valid for at least {@link MIN_TOKEN_VALIDITY_SECONDS}.
   *
   * Redirects to login only when Keycloak actively rejected the session. If
   * Keycloak was merely unreachable, the current token is returned so the
   * caller can attempt the request: a 401 is recoverable, a redirect is not.
   *
   * @param force Refresh even if the current token has validity left. Use after
   * a rejected request, where the token is known bad despite its expiry claim.
   */
  ensureFreshToken: async (force = false): Promise<string | undefined> => {
    if (!keycloakClient?.token) {
      return undefined;
    }
    try {
      await refreshSession(force ? FORCE_REFRESH : MIN_TOKEN_VALIDITY_SECONDS);
    } catch (error) {
      if (error instanceof SessionExpiredError) {
        redirectToLogin('refresh token rejected');
        return undefined;
      }
      logSessionEvent('could not reach keycloak, reusing current token', error);
    }
    return keycloakClient?.token;
  },

  getUsername: () => keycloakClient?.tokenParsed?.preferred_username,

  getDisplayProfile: (): { name: string; subtitle?: string } => {
    if (!keycloakClient?.token) {
      return { name: 'User' };
    }
    const parsed = keycloakClient.tokenParsed;
    const username =
      typeof parsed?.preferred_username === 'string' ? parsed.preferred_username.trim() : '';
    const email = typeof parsed?.email === 'string' ? parsed.email.trim() : '';
    const first = typeof parsed?.given_name === 'string' ? parsed.given_name.trim() : '';
    const last = typeof parsed?.family_name === 'string' ? parsed.family_name.trim() : '';
    const fullName = [first, last].filter(Boolean).join(' ');
    const name = fullName || username || email || 'User';
    const subtitleRaw = email || username || undefined;
    const subtitle = subtitleRaw && subtitleRaw !== name ? subtitleRaw : undefined;
    return { name, subtitle };
  },

  // hasRole: (roles) => roles.some((role) => keycloakClient.hasRealmRole(role)),
  getUserObject: () =>
    ({
      id: keycloakClient?.tokenParsed?.sub,
      email: keycloakClient?.tokenParsed?.email,
      firstName: keycloakClient?.tokenParsed?.given_name,
      lastName: keycloakClient?.tokenParsed?.family_name,
    }) as Partial<User>,
};
